/**
 * onboarding-beats-shared — primitives shared by OnboardingBeats and the
 * phase beats (OnboardingAnalysisBeat, OnboardingCommandsBeat). Extracted
 * verbatim so the beat files can import them without a runtime cycle
 * (OnboardingBeats imports the beat components).
 */

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';

// ── Shared beat types ─────────────────────────────────────────────────────────
// Defined here so phase-beat components (OnboardingAnalysisBeat,
// OnboardingCommandsBeat) can import them without creating a cycle with
// OnboardingBeats.tsx, which imports those component files.
// OnboardingBeats.tsx re-exports everything from here for back-compat.

export type BeatKind =
  | 'welcome' | 'ask' | 'demo-intro' | 'scan' | 'assessment'
  | 'calendar' | 'contacts' | 'demo' | 'pages' | 'walkthrough'
  | 'analysis' | 'commands'
  | 'team-welcome' | 'team-owner-welcome';

export interface AssessRow { icon: string; text: string; bold?: boolean }
export interface NetContact { name: string; email: string; note: string; aiEnhanced?: boolean }

/** The scan-derived data, carried by every post-scan beat so each message stands alone. */
export interface Bundle {
  name: string;
  company: string;
  initial: string;
  topContact: string;
  /** Third-person voice (demo persona "Boris is…") vs second-person ("you are…"). */
  third: boolean;
  /** Subject pronoun/name for shared intro copy: "Boris" / "you". */
  subject: string;
  /** Possessive for shared intro copy: "Boris's" / "your". */
  possessive: string;
  /** Prose "read" of the person, rendered as sections with a click-for-more. */
  assessSections: Array<{ title: string; body: string }>;
  assess: AssessRow[];
  calRows: AssessRow[];
  calClosingBold: string;
  contacts: NetContact[];
}

export interface BeatPayload {
  kind: BeatKind;
  bundle?: Bundle;
  /** for kind 'demo' */ which?: 'agents' | 'email' | 'calendar' | 'contacts' | 'drive';
  /** Personalization data threaded from OnboardingAnalysisBeat → OnboardingCommandsBeat. */
  analysis?: {
    upcomingCount?: number;
    topFirst?: string;            // e.g. "Neda"
    recurringFirst?: string;      // first recurring meeting title
    nextTripDestination?: string; // e.g. "Paris (CDG)"
    nextTripDate?: string;        // e.g. "Jul 3"
  };
}

export interface BeatContext {
  isCurrent: boolean;
  token?: string | null;
  workspaceId?: string;
  userEmail?: string | null;
  userName?: string | null;
  /** Display name of the team workspace (from ContainerAccess.containerName). Used by TeamWelcomeBeat. */
  workspaceName?: string | null;
  isDemo?: boolean;
  isStreaming?: boolean;
  advance: (kind: BeatKind, extra?: Partial<BeatPayload>) => void;
  /** 'use-existing' = a real account is already connected with the needed
   *  scopes; skip the connect prompt and run the personalized walkthrough on
   *  real data (advances onboarding to email_connected, no OAuth popup). */
  connect: (kind: 'oauth.google.email' | 'oauth.microsoft.outlook' | 'demo' | 'use-existing') => void;
  runPrompt: (prompt: string) => void;
  /** Run a slash command (e.g. '/today') as if the user typed it. */
  runCommand?: (cmd: string) => void;
  onAssessmentFeedback?: (spotOn: boolean) => void;
  /** Fire-and-forget: as soon as the scan data lands, persist the user's
   *  contacts + profile-to-Drive silently (no UI). Runs once per workspace. */
  onScanComplete?: (bundle: Bundle) => void;
  onComplete: () => void;
  /** Open a calendar event (omit id to just surface the calendar module). */
  onOpenEvent?: (eventId?: string) => void;
  /** Open a contact (the ContactCardPopover). */
  onOpenContact?: (contactId: string) => void;
  /** Open a note in the Drive module. */
  onOpenNote?: (noteId: string) => void;
  /** Open the Pages module (reports / generated web pages). */
  onOpenPages?: () => void;
  /** Open the Agents module. */
  onOpenAgents?: () => void;
  /** Open the Team modal (invite/members). Used by the team-owner onboarding. */
  onOpenTeam?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────

/** Minimal beat context fields used by fetchObjects. Structurally compatible with
 *  the full BeatContext above — no import needed, no cycle. */
interface FetchCtx {
  workspaceId?: string;
  token?: string | null;
}

// Shared typing pace. TICK_MS is the cadence; CPS controls how many characters
// land per tick (step = round(cps/30)). Tuned slightly faster than before for a
// snappier-but-still-readable feel, and kept in ONE place so every typed surface
// (multi-line intros + single-line demo captions) shares the same rhythm.
export const TYPE_TICK_MS = 36;
export const TYPE_LINE_GAP_MS = 110;
export const TYPE_CPS = 90;

/** Types a beat's lines in sequence (each paragraph after the previous finishes),
 *  with a blinking caret. `active` = the live beat; past/replayed beats render the
 *  full text at once. Fires onComplete when done. `lines` must be a stable reference
 *  (module-level const) so the typing effect doesn't restart each render. */
export function TypedLines({ lines, active, className = 'oa-welcome-body', cps = TYPE_CPS, onComplete }: { lines: string[]; active: boolean; className?: string; cps?: number; onComplete?: () => void }) {
  const [idx, setIdx] = useState(active ? 0 : lines.length);
  const [count, setCount] = useState(0);
  const doneRef = useRef(onComplete);
  doneRef.current = onComplete;
  useEffect(() => { if (active) { setIdx(0); setCount(0); } else { setIdx(lines.length); } }, [active, lines.length]);
  useEffect(() => {
    if (!active) return;
    if (idx >= lines.length) { doneRef.current?.(); return; }
    const text = lines[idx];
    if (count >= text.length) {
      const t = setTimeout(() => { setIdx((i) => i + 1); setCount(0); }, TYPE_LINE_GAP_MS);
      return () => clearTimeout(t);
    }
    const step = Math.max(1, Math.round(cps / 30));
    const id = setInterval(() => setCount((c) => Math.min(c + step, text.length)), TYPE_TICK_MS);
    return () => clearInterval(id);
  }, [active, idx, count, lines, cps]);
  return (
    <>
      {lines.map((text, i) => {
        if (active && i > idx) return null;
        const typing = active && i === idx;
        const shown = typing ? text.slice(0, count) : text;
        return <div key={i} className={className}>{shown}{typing && shown.length < text.length ? <span className="oa-type-caret" /> : null}</div>;
      })}
    </>
  );
}

// Safety net for the "reveal the advance button only after the content has
// faded in" gating: if the fade's `animationend` never fires (e.g. a future
// reduced-motion rule sets `.obr-fade { animation: none }`), reveal anyway after
// a generous delay so the button can never get permanently stuck. animationend
// normally fires in ~1s, well before this.
export function useRevealFallback(active: boolean, reveal: () => void) {
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(reveal, 7000);
    return () => clearTimeout(t);
    // reveal is a stable setState updater; intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

export function AdvanceBubble({ label, isCurrent, onAdvance }: { label: string; isCurrent: boolean; onAdvance: () => void }) {
  const [leaving, setLeaving] = useState(false);
  // A past beat's bubble must sit on the user (right) side. Wrap it in a
  // full-width flex row that justifies to the end so it right-aligns regardless
  // of the parent (block .oa-welcome vs flex-column .obr where the demo beats
  // live) — self-alignment on the bubble alone proved unreliable across both.
  if (!isCurrent) return (
    <div className="oa-userbubble-row">
      <div className="oa-userbubble oa-userbubble--sent">{label}</div>
    </div>
  );
  // Click → the button lifts away (is-leaving), then we advance — and the beat's
  // right-aligned blue chat bubble slides in to take its place.
  const go = () => { if (leaving) return; setLeaving(true); setTimeout(onAdvance, 240); };
  // `obr-fade` makes the button fade in (it mounts only once its section's
  // content has finished revealing) so it matches the rest of the onboarding.
  return <button type="button" className={`oa-next-btn${leaving ? '' : ' obr-fade'}${leaving ? ' is-leaving' : ''}`} disabled={leaving} onClick={go}>{label}</button>;
}

// Fetch workspace objects by type using the beat's EXPLICIT workspaceId. ScanBeat
// proves apiFetch+workspaceId works in onboarding; listObjects (which resolves the
// workspace via getActiveWorkspaceId) silently no-ops in this context. `pick` reads
// a field whether the API flattened attributes to top level or left them nested.
export async function fetchObjects(ctx: FetchCtx, type: string, limit = 400): Promise<any[]> {
  if (!ctx.workspaceId) return [];
  try {
    // Force X-Workspace-Id from the beat's known workspaceId. apiFetch otherwise
    // derives it from getSelectedWorkspaceId(), which can be empty after a reload
    // (it only overrides our header when truthy), so the typed fetch would
    // silently scope to nothing and come back empty.
    const res = await apiFetch<any>(
      `/workspaces/${ctx.workspaceId}/${type}s?limit=${limit}`,
      { authToken: ctx.token ?? undefined, headers: { 'X-Workspace-Id': ctx.workspaceId } },
    );
    return (Array.isArray(res) ? res : (res?.objects ?? res?.items ?? [])) as any[];
  } catch { return []; }
}
export function pick(o: any, key: string): any { return o?.[key] ?? o?.attributes?.[key]; }
export function isDemoSourced(o: any): boolean { return String(pick(o, 'source') ?? '').startsWith('demo-'); }

// Parse an event start to a timestamp, reading a bare date / UTC-midnight as a
// LOCAL day so all-day events don't shift back a day in negative-offset zones.
export function eventStartTs(raw: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m && /^\d{4}-\d{2}-\d{2}($|T00:00)/.test(raw)) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  return new Date(raw).getTime();
}

export const TOPIC_LABEL: Record<string, string> = {
  'bitcoin/crypto': 'Crypto', 'book/publishing': 'Writing', 'fundraising/VC': 'Fundraising',
  'legal/tax': 'Legal & tax', 'sales/deals': 'Deals', 'hiring': 'Hiring', 'travel': 'Travel', 'product/eng': 'Product',
  'events/orders': 'Events & orders',
};

export function initials(name: string, email: string): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2)).toUpperCase();
}
